import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon, ArrowRightIcon, LeafIcon } from 'lucide-react';
import { TreatmentList } from './TreatmentList';
import { SalonTeam } from './SalonTeam';
import { Faq } from '../shared/Faq';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { ParallaxMedia } from '../shared/ParallaxMedia';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { salonFaq } from '../../data/faq';
import { founder, salon } from '../../data/content';
import { policyCopy } from '../../data/policies';
import { usePages, useSite } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { listNl } from '../../core/format/text';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnOnClay, btnPrimaryLg, btnSecondaryLg, chipCls } from '../../ui/styles';
import { belowHeader, closingHeading, quietLink, sectionHeading } from '../../ui/sections';
const categories = ['Anti-ageing', 'Huidverbetering', 'Ontspanning', 'Losse applicaties'] as const;
type Filter = (typeof categories)[number] | 'Alles';
const filters: readonly Filter[] = ['Alles', ...categories];

/**
 * De salonpagina: hier valt de beslissing om te boeken. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wie behandelt me en kan ik boeken (hero, sand) · wat kies ik (alle
 * behandelingen, shell; de categorieën blijven bovenin staan) · bij wie (collega's, sand; alleen
 * als Sarah ze op de site zet) · waarom werkt het (bindweefselmassage, klei) · hoe houd ik het
 * vast (van salon naar thuis, sand) · praktisch (vragen, shell) · twijfel (blush).
 * Grote beelden bewegen licht binnen hun kader (`ParallaxMedia`), zoals op de homepage.
 */
export function Treatments() {
  const pages = usePages();
  const {
    policy
  } = useSite();
  const {
    openBooking
  } = useOverlayActions();
  const {
    treatments,
    brandNames
  } = useCatalog();
  const [active, setActive] = useState<Filter>('Alles');
  const copy = pages.treatments;
  const {
    bindweefsel
  } = copy;
  // De losse bindweefselmassage: een losse applicatie met bindweefselmassage (in het beheer aan te vinken).
  const bindweefselLos = treatments.find(t => t.bindweefsel && t.category === 'Losse applicaties');
  const heroMedia = mediaOr(copy.hero.media, {
    image: founder.atWork,
    alt: `${founder.name} tijdens een gezichtsbehandeling`
  });
  const massageMedia = mediaOr(bindweefsel.media, {
    image: founder.atWork,
    alt: ''
  });
  const allGroups = categories.map(c => ({
    category: c,
    items: treatments.filter(t => t.category === c)
  })).filter(g => g.items.length > 0);
  // Een categorie zonder behandelingen (alles offline gezet) is geen filter: die gaf een lege pagina.
  const visibleFilters = filters.filter(f => f === 'Alles' || allGroups.some(g => g.category === f));
  const groups = allGroups.filter(g => active === 'Alles' || g.category === active);
  return <>
      <PageHero titleId="salon-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8552" data-mp="288ph38note7m" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              {fill(copy.hero.treatedBy)} <span className="text-ink" data-id="element-8553" data-mp="2zyvbuv8ziozn">{founder.name}</span>
            </>,
      sub: `Alleen op afspraak · ${policyCopy.experience} ervaring`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={() => openBooking()} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8554" data-mp="2m7yw4505q2a0">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <a href="#behandelingen" className={btnSecondaryLg} data-id="element-8555" data-mp="3evkyxb1k92vt">
                  {fill(copy.hero.secondaryCta)}
                </a>}
            </>}
    // De agenda zit vaak vol: geef wie moet wachten meteen iets te doen.
    footnote={<p data-id="element-8556" data-mp="2pjb9tx77pekr">
            <span className="text-ink" data-id="element-8557" data-mp="2pjl1dp7wntsk">{fill(copy.hero.agenda.title)}</span> {fill(copy.hero.agenda.body)}{' '}
            {copy.hero.agenda.cta && <Link to="/routines" className="whitespace-nowrap text-ink underline underline-offset-4 transition-colors duration-150 ease-soft hover:text-clay" data-id="element-8558" data-mp="3i773et1hiqdb">
                {fill(copy.hero.agenda.cta)}
              </Link>}
          </p>} data-id="element-8551" data-mp="2h7kmwjmtkmae" />

      {/* Alle behandelingen, direct na de hero: wie hier komt, wil kiezen. */}
      <section id="behandelingen" className="scroll-mt-24 bg-shell py-20 sm:py-24 lg:py-32" aria-labelledby="behandelingen-title" data-id="element-8559" data-mp="4zdsf19ure72">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8560" data-mp="9qcaa5ahokcf">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8561" data-mp="9rx8c4w0ilf1">
            <h2 id="behandelingen-title" className={`${sectionHeading} text-ink lg:col-span-6`} data-id="element-8562" data-mp="j332t1q74x7h">
              Alle behandelingen
            </h2>
            {copy.includes.length > 0 && <div className="lg:col-span-6" data-id="element-8563" data-mp="stxn40jvtmqa">
                <p className="text-[14px] text-ink" data-id="element-8564" data-mp="nvw1p8yzhbbn">{fill(copy.hero.includesTitle)}</p>
                <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1.5" data-id="element-8565" data-mp="61vha966l0d">
                  {copy.includes.map(item => <li key={item} className="flex items-center gap-1.5 text-[14px] text-muted" data-id="element-8566" data-mp="3sam69cee1awr">
                      <CheckIcon className="h-3.5 w-3.5 text-sage" strokeWidth={2} aria-hidden="true" data-id="element-8567" data-mp="a4r38a7cqp2w" />
                      {item}
                    </li>)}
                </ul>
              </div>}
          </div>

          {/* Eén regel die je kunt schuiven, op elk scherm; blijft net onder de header staan zolang je door de lijst scrolt. */}
          {visibleFilters.length > 2 && <div className={`sticky ${belowHeader} z-sticky -mx-6 mt-10 bg-shell py-3 lg:-mx-10`} data-id="element-8568" data-mp="1ojy1t9uyjcrv">
              <div className="flex gap-2 overflow-x-auto px-6 [scrollbar-width:none] lg:px-10 [&::-webkit-scrollbar]:hidden" role="group" aria-label="Toon categorie" data-id="element-8569" data-mp="1sl9rffdtzy02">
                {visibleFilters.map(c => <button key={c} type="button" aria-pressed={active === c} onClick={e => {
              setActive(c);
              // Viel de lijst al onder de balk, dan terug naar het begin van de lijst.
              const list = document.getElementById('behandelingen-lijst');
              const bar = e.currentTarget.parentElement?.parentElement;
              if (list && bar && list.getBoundingClientRect().top < bar.getBoundingClientRect().bottom) {
                window.scrollBy({
                  top: list.getBoundingClientRect().top - bar.getBoundingClientRect().bottom - 8
                });
              }
            }} className={`${chipCls(active === c)} shrink-0`} data-id="element-8570" data-mp="14xid668qdaw5">
                    {c}
                  </button>)}
              </div>
            </div>}

          <div id="behandelingen-lijst" className="mt-9 space-y-16 lg:space-y-20" data-id="element-8571" data-mp="4qwi051w18eu">
            {groups.map(group => <div key={group.category} className="lg:grid lg:grid-cols-12 lg:gap-16" data-id="element-8572" data-mp="3s25yf5jhda07">
                <h3 className="text-[14px] text-clay lg:col-span-3 lg:pt-8" data-id="element-8573" data-mp="3s01n42j958pw">{group.category}</h3>
                <div className="mt-3 lg:col-span-9 lg:mt-0" data-id="element-8574" data-mp="3n0gj3dp86a6b">
                  <TreatmentList items={group.items} data-id="element-8575" data-mp="3i59n6s2769ry" />
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <SalonTeam data-id="element-8576" data-mp="393a6k0v3h7pj" />

      {/* Bindweefselmassage: waarom het werkt, het ene verhaal op klei */}
      <section className="bg-clay text-shell" aria-labelledby="bindweefsel" data-id="element-8577" data-mp="10ulhs71do44g">
        <div className="grid lg:grid-cols-12" data-id="element-8578" data-mp="10shglwuv5j7v">
          <ParallaxMedia media={massageMedia} sizes="(min-width: 1024px) 42vw, 100vw" range={40} className="aspect-[4/3] w-full sm:aspect-[16/10] lg:order-2 lg:col-span-5 lg:aspect-auto lg:h-full lg:min-h-[680px]" data-id="element-8579" data-mp="15tom1p9ja6bd" />

          <div className="px-6 py-20 sm:px-10 lg:order-1 lg:col-span-7 lg:py-28 lg:pl-10 lg:pr-16 xl:pl-[max(2.5rem,calc((100vw-80rem)/2+2.5rem))]" data-id="element-8580" data-mp="i442p59gx71k">
            <p className="text-[14px] text-shell" data-id="element-8581" data-mp="mw3xykelfbrt">{bindweefsel.title}</p>
            <h2 id="bindweefsel" className={`mt-5 max-w-xl ${sectionHeading}`} data-id="element-8582" data-mp="86po7b73usbw">
              {fill(bindweefsel.claim)}
            </h2>
            <p className="mt-7 max-w-lg text-[17px] leading-relaxed text-shell" data-id="element-8583" data-mp="d5115usp5iyc">{fill(bindweefsel.intro)}</p>

            <div className="mt-12 grid gap-10 sm:grid-cols-2 sm:gap-12" data-id="element-8584" data-mp="3vcgw0eunx9md">
              <BulletList title="Voor wie is het goed?" items={bindweefsel.forWho} data-id="element-8585" data-mp="3620ozi34tmf" />
              <BulletList title="Wat doet het?" items={bindweefsel.effects} data-id="element-8586" data-mp="3goglbs4mymeu" />
            </div>

            {bindweefsel.rhythm && <p className="mt-8 text-[15px] text-shell" data-id="element-8587" data-mp="3bqf319f1ar4f">{fill(bindweefsel.rhythm)}</p>}

            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-6" data-id="element-8588" data-mp="3pp538xx5h6x0">
              {bindweefsel.cta && <button type="button" onClick={() => openBooking(bindweefselLos?.slug)} aria-haspopup="dialog" className={btnOnClay} data-id="element-8589" data-mp="3h5fxnlk30t6">
                  {fill(bindweefsel.cta)}
                </button>}
              {bindweefsel.secondaryCta && <Link to="/webshop" className={`${quietLink} text-shell`} data-id="element-8590" data-mp="c8xnnqj73vu2">
                  {fill(bindweefsel.secondaryCta)}
                  <ArrowRightIcon className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" data-id="element-8591" data-mp="8a80j68cuzx8" />
                </Link>}
            </div>
          </div>
        </div>
      </section>

      {/* Van salon naar thuis: de brug naar de webshop */}
      <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby="salon-bridge" data-id="element-8592" data-mp="1vihday8vhqhk">
        <div className="mx-auto grid max-w-content items-center gap-12 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8593" data-mp="1qpobai4sgw96">
          <ParallaxMedia media={{
          image: founder.withProducts,
          alt: `${founder.name} met de producten waar ze mee werkt`,
          focus: {
            x: 50,
            y: 35
          }
        }} sizes="(min-width: 1024px) 40vw, 100vw" className="aspect-[4/3] w-full sm:aspect-[16/10] lg:col-span-5 lg:aspect-[4/5]" data-id="element-8594" data-mp="1mpxf1ywhtnx9" />
          <div className="lg:col-span-7" data-id="element-8595" data-mp="2afhw2c936b3w">
            <h2 id="salon-bridge" className={`${sectionHeading} text-ink`} data-id="element-8596" data-mp="25fdfync3bie7">
              <HeadingText heading={copy.bridge.heading} accentBlock data-id="element-8597" data-mp="25hggw2es6vxp" />
            </h2>
            {copy.bridge.body && <p className="mt-7 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-8598" data-mp="u0gpra466n59">{fill(copy.bridge.body)}</p>}
            {brandNames.length > 0 && <p className="mt-7 flex max-w-xl items-start gap-3 border-t border-ink/15 pt-6 text-[15px] leading-relaxed text-muted" data-id="element-8599" data-mp="p0lzn8s7y1l9">
                <LeafIcon className="mt-0.5 h-4 w-4 shrink-0 text-sage" strokeWidth={1.5} aria-hidden="true" data-id="element-8600" data-mp="evlyqptslfz7" />
                100% vegan: {listNl(brandNames)}.
              </p>}
            {/* Knoppen zonder tekst (leeg gelaten in Paginateksten) niet tonen, zoals op de andere pagina's. */}
            {(copy.bridge.cta || copy.bridge.secondaryCta) && <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-8601" data-mp="xnercj4y8z6e">
                {copy.bridge.cta && <Link to="/routines" className={btnPrimaryLg} data-id="element-8602" data-mp="12p4360zzxgvj">
                    {fill(copy.bridge.cta)}
                  </Link>}
                {copy.bridge.secondaryCta && <Link to="/webshop" className={btnSecondaryLg} data-id="element-8603" data-mp="1cj4fonxlxoea">
                    {fill(copy.bridge.secondaryCta)}
                  </Link>}
              </div>}
          </div>
        </div>
      </section>

      {/* Praktisch */}
      <section className="bg-shell py-20 sm:py-24 lg:py-32" data-id="element-8604" data-mp="36cnq64gyamrt">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8605" data-mp="31d1e3ycuzckl">
          <Faq items={salonFaq} title={fill(copy.faqTitle)} data-id="element-8606" data-mp="31emaltiqh5l3" />
        </div>
      </section>

      {/* Afsluiter: voor wie nog twijfelt */}
      <section className="bg-blush py-20 sm:py-24" aria-labelledby="salon-closing" data-id="element-8607" data-mp="3bb8q02saj4fq">
        <div className="mx-auto grid max-w-content gap-8 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8608" data-mp="3paqlf103dfy2">
          <div className="lg:col-span-7" data-id="element-8609" data-mp="3kbx38v9v10rk">
            <h2 id="salon-closing" className={`${closingHeading} text-ink`} data-id="element-8610" data-mp="3kb486rk9jzp9">
              <HeadingText heading={copy.closing.heading} data-id="element-8611" data-mp="2u46vrg92r95" />
            </h2>
            <p className="mt-4 max-w-xl text-[17px] leading-relaxed text-muted" data-id="element-8612" data-mp="3u1qj48h1c21m">
              {copy.closing.body && `${fill(copy.closing.body)} `}
              {policyCopy.consultCredit}.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row lg:col-span-5 lg:justify-end" data-id="element-8613" data-mp="7v1q87czxrc8">
            {copy.closing.cta && <button type="button" onClick={() => openBooking(policy.consult.slug)} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8614" data-mp="qj2dsaah4hop">
                {fill(copy.closing.cta)}
              </button>}
            {copy.closing.secondaryCta && <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnSecondaryLg} data-id="element-8615" data-mp="mgfs6r4ebszy">
                {fill(copy.closing.secondaryCta)}
                <span className="sr-only" data-id="element-8616" data-mp="7qqejwf49wis">{shopLabels.viaWhatsApp}</span>
              </a>}
          </div>
        </div>
      </section>
    </>;
}
function BulletList({
  title,
  items
}: {
  title: string;
  items: string[];
}) {
  if (items.length === 0) return null;
  return <div data-id="element-8617" data-mp="2tht9myo9cdb">
      <h3 className="font-display text-2xl font-light" data-id="element-8618" data-mp="2t80xsq5mt4g">{title}</h3>
      <ul className="mt-5 divide-y divide-shell/25 border-y border-shell/25" data-id="element-8619" data-mp="3kzme3pglmz1s">
        {items.map(item => <li key={item} className="py-4 text-[15px] leading-snug text-shell" data-id="element-8620" data-mp="3pwcvqh81y2pb">
            {item}
          </li>)}
      </ul>
    </div>;
}